import type { LoginDto } from "@authentication/data/dto/login.dto";
import type {
    PasswordRecoveryBody,
    PasswordResetBody,
} from "@authentication/data/dto/password-recovery.dto";
import { LoginUserMapper } from "@authentication/data/mappers/login-user.mapper";
import { InvalidCredentialsError } from "@authentication/domain/errors/login/invalid-credentials.error";
import { LoginBlockedError } from "@authentication/domain/errors/login/login-blocked.error";
import type { LoginError } from "@authentication/domain/errors/login/login.error";
import type { LoginUser } from "@authentication/domain/models/login-user.model";
import type { HttpError, IotApiClient } from "@iot-platform/core";
import {
    coreTypes,
    FallbackError,
    HttpTooManyRequestsError,
    HttpUnauthorizedError,
} from "@iot-platform/core";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";
import { err, ok } from "neverthrow";

const PASSWORD_RECOVERY_PATH = "/users/password/";
const LOGOUT_PATH = "/users/sign_out";
const LOGIN_PATH = "/users/sign_in";

@injectable()
export class AuthenticationRemoteDatasource {
    constructor(
        @inject(LoginUserMapper)
        private readonly loginUserMapper: LoginUserMapper,
        @inject(coreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
    ) {}

    login(email: string, password: string): ResultAsync<LoginUser, LoginError> {
        const body = this.loginUserMapper.mapToBodyDto(email, password);

        return this.iotApiClient.http
            .post<LoginDto>(LOGIN_PATH, body, {
                // A 401 here is just wrong credentials, not an expired
                // session, so it must not trigger the global logout flow.
                emitUnauthorizedEvent: false,
            })
            .mapErr((error) => {
                if (error instanceof HttpUnauthorizedError) {
                    return new InvalidCredentialsError();
                }

                if (error instanceof HttpTooManyRequestsError) {
                    return new LoginBlockedError(
                        this.loginUserMapper.mapBlockedUntil(error.data),
                    );
                }

                return new FallbackError();
            })
            .andThen((response) => {
                const token = response.data.auth_token;

                const loginUser = this.loginUserMapper.map(
                    response.data,
                    token,
                );

                if (!loginUser) return err(new FallbackError());

                return ok(loginUser);
            });
    }

    logout(): ResultAsync<true, HttpError> {
        const logoutResult = this.iotApiClient.http.delete(LOGOUT_PATH);
        this.iotApiClient.disableAuth();

        return logoutResult.map(() => true);
    }

    requestMailToResetPassword(
        login: string,
    ): ResultAsync<true, FallbackError> {
        const body: PasswordRecoveryBody = {
            user: {
                login,
            },
        };

        return this.iotApiClient.http
            .post(PASSWORD_RECOVERY_PATH, body)
            .mapErr(() => new FallbackError())
            .andThen(() => ok(true as const));
    }

    resetPassword(
        token: string,
        password: string,
        password_confirmation: string,
    ): ResultAsync<true, FallbackError> {
        const body: PasswordResetBody = {
            user: {
                reset_password_token: token,
                password,
                password_confirmation,
            },
        };

        this.iotApiClient.disableAuth();

        return this.iotApiClient.http
            .put(PASSWORD_RECOVERY_PATH, body)
            .mapErr(() => new FallbackError())
            .map(() => true);
    }
}
